/**
 * FontAwesome Icons Tab Content - Search results or category grid
 * Uses deferred rendering for snappy tab switching
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FA_CATEGORY_NAMES, searchFAIcons, getFAIconsByCategory } from '../data/fontawesome';
import FAIconSection from './FAIconSection';

const IconsTabContent = ({
  searchResults,
  recentIcons,
  currentValue,
  resolvedColor,
  selectedColor,
  categoryRefs,
  onSelect,
}) => {
  const [isReady, setIsReady] = useState(false);

  // Defer heavy rendering to next frame for snappy tab switch
  useEffect(() => {
    setIsReady(false);
    const id = requestAnimationFrame(() => {
      setIsReady(true);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  // Show loading state briefly
  if (!isReady) {
    return (
      <div className="ip-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
      </div>
    );
  }

  // Search mode
  if (searchResults) {
    return (
      <div className="ip-section">
        <div className="ip-section__header">{searchResults.length} results</div>
        {searchResults.length === 0 ? (
          <div className="ip-empty">No results found</div>
        ) : (
          <div className="ip-section__grid ip-section__grid--icons">
            {searchResults.map((item, i) => (
              <button
                key={i}
                className={`ip-btn ip-btn--icon ${currentValue === item.name ? 'ip-btn--selected' : ''}`}
                style={{ color: resolvedColor }}
                onClick={() => onSelect({ 
                  type: 'fontawesome', 
                  value: item.name,
                  color: selectedColor 
                })}
                title={item.displayName}
              >
                <FontAwesomeIcon icon={item.icon} />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Browse mode
  return (
    <>
      {/* Recent icons */}
      {recentIcons.length > 0 && (
        <div className="ip-section">
          <div className="ip-section__header">Recent</div>
          <div className="ip-section__grid ip-section__grid--icons">
            {recentIcons.map((iconName, i) => {
              const iconData = searchFAIcons(iconName)?.[0];
              if (!iconData) return null;
              return (
                <button
                  key={i}
                  className={`ip-btn ip-btn--icon ${currentValue === iconName ? 'ip-btn--selected' : ''}`}
                  style={{ color: resolvedColor }}
                  onClick={() => onSelect({ 
                    type: 'fontawesome', 
                    value: iconName,
                    color: selectedColor 
                  })}
                  title={iconData.displayName}
                >
                  <FontAwesomeIcon icon={iconData.icon} />
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Category sections */}
      {FA_CATEGORY_NAMES.map(cat => (
        <div key={cat} ref={el => categoryRefs.current[cat] = el}>
          <FAIconSection
            category={cat}
            icons={getFAIconsByCategory(cat)}
            onSelect={onSelect}
            currentValue={currentValue}
            color={resolvedColor}
            colorValue={selectedColor}
          />
        </div>
      ))}
    </>
  );
};

export default IconsTabContent;
